<script setup lang="ts">
/**
 * 設定／更換一個站台機密（批151 C6；營運主管、網頁專屬）。
 *
 * ★ 值的處理：輸入框 `type=password`、**不回填**（後台本來就拿不到值）、送出或關窗即清空；
 *   錯誤訊息一律不回聲輸入（`lib/siteSecrets.ts`）。值只在這一次 `PUT` 裡出現。
 * ★ 值與現值相同＝後端不寫也不派工（`changed: false`）——要把同一把值重新寫進站台用〈重送〉，
 *   這裡講清楚，免得操作者以為「按了沒反應」。
 * ★ 點背景不關（`close-on-backdrop=false`）：已經貼上的值被誤點清掉，只會讓人再去翻一次金鑰。
 */
import { computed, ref, watch } from 'vue'

import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import * as sitesApi from '@/api/sites'
import type { SiteSecretField, SiteSecretWriteResult } from '@/api/sites'
import { SECRET_MAX_LEN, SECRET_MIN_LEN, secretLast4Text, siteSecretErrText } from '@/lib/siteSecrets'

const props = defineProps<{
  open: boolean
  siteId: string
  siteSlug: string
  field: SiteSecretField | null
}>()
/**
 * `failed`：送出失敗（批151 R3-15）。錯誤照樣顯示在框內、框不關；宿主收到後
 * 重讀站台機密狀態——失敗常是「狀態變了」（別人剛改過、站台不能派工、主鑰沒了），畫面不能停在舊的。
 */
const emit = defineEmits<{ close: []; done: [SiteSecretWriteResult]; failed: [] }>()

const value = ref('')
const busy = ref(false)
const error = ref('')

watch(
  () => props.open,
  () => {
    // 開與關都清：值不留在元件裡（下次開窗也不會看到上一把）。
    value.value = ''
    error.value = ''
  },
)

const trimmedLen = computed(() => value.value.trim().length)
const lengthOk = computed(() => trimmedLen.value >= SECRET_MIN_LEN && trimmedLen.value <= SECRET_MAX_LEN)
const replacing = computed(() => !!props.field?.configured)

async function submit() {
  if (!props.field || !lengthOk.value || busy.value) return
  busy.value = true
  error.value = ''
  try {
    const res = await sitesApi.putSiteSecret(props.siteId, props.field.key, value.value)
    value.value = ''
    emit('done', res)
  } catch (e) {
    error.value = siteSecretErrText(e, replacing.value ? '更換失敗' : '設定失敗')
    emit('failed')
  } finally {
    busy.value = false
  }
}

function close() {
  value.value = ''
  emit('close')
}
</script>

<template>
  <UiModal
    :open="open"
    :title="`${replacing ? '更換' : '設定'}站台機密 — ${siteSlug}`"
    width="520px"
    :close-on-backdrop="false"
    @close="close"
  >
    <template v-if="field">
      <div class="meta">
        <div class="row"><span class="k">欄位</span><span class="v">{{ field.label }}</span></div>
        <div class="row"><span class="k">環境變數</span><span class="v"><code class="mono">{{ field.env }}</code></span></div>
        <div v-if="replacing" class="row">
          <span class="k">目前</span><span class="v">{{ secretLast4Text(field) }}</span>
        </div>
      </div>
      <p v-if="field.hint" class="subtle hint">{{ field.hint }}</p>

      <form class="f" autocomplete="off" @submit.prevent="submit">
        <label>
          <span>{{ replacing ? '新的值' : '值' }}</span>
          <!-- ★ type=password＋autocomplete=off：不讓瀏覽器回填或記住；畫面上永遠看不到值。 -->
          <input
            v-model="value"
            type="password"
            autocomplete="off"
            spellcheck="false"
            :maxlength="SECRET_MAX_LEN + 64"
            :placeholder="`${SECRET_MIN_LEN}～${SECRET_MAX_LEN} 字，不含空白、引號、$、# 或反斜線`"
          />
        </label>
      </form>
      <p class="subtle hint">
        值只在這次送出時傳給授權中心，加密保存後由 agent 寫進站台；後台之後只看得到末四碼，查不回值。
        {{ replacing ? '更換後舊值立刻釋出（可以給別站使用）。' : '' }}
      </p>
      <p v-if="value && !lengthOk" class="warn-text">長度必須介於 {{ SECRET_MIN_LEN }}～{{ SECRET_MAX_LEN }} 字。</p>
      <p v-if="error" class="warn-text">{{ error }}</p>
    </template>

    <template #foot>
      <UiButton @click="close">取消</UiButton>
      <UiButton variant="primary" :loading="busy" :disabled="!lengthOk" @click="submit">
        {{ replacing ? '更換並寫入站台' : '設定並寫入站台' }}
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.meta {
  display: grid;
  gap: 4px;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: var(--bg);
}
.row {
  display: flex;
  gap: var(--sp-3);
  font-size: var(--fs-13);
}
.k {
  min-width: 72px;
  color: var(--fg-3);
}
.v {
  color: var(--fg);
  word-break: break-all;
}
.hint {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-12);
}
.f {
  margin-top: var(--sp-3);
}
.f label {
  display: grid;
  gap: 4px;
}
.f label > span {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.f input {
  width: 100%;
  min-width: 0;
}
.warn-text {
  margin: var(--sp-2) 0 0;
  color: var(--warning);
  font-size: var(--fs-12);
}
</style>
